/**
 * Persistence Hook
 * Bridges Zustand state with IndexedDB for permanent storage
 */

import { useEffect, useRef } from 'react';
import { getPersistenceManager } from '../lib/persistence';
import { useAppStore } from '../store/app-store';

export function usePersistence() {
  const pm = useRef(null);
  const initialized = useRef(false);
  const { conversations, addConversation, updateConversation } = useAppStore();

  // Initialize persistence manager
  useEffect(() => {
    if (initialized.current) return;

    const initPersistence = async () => {
      try {
        pm.current = getPersistenceManager();
        await pm.current.initialize();

        // Load conversations from IndexedDB
        const savedConversations = await pm.current.getAllConversations();
        console.log('[Persistence] Loaded', savedConversations.length, 'conversations');

        // Restore each conversation with its messages
        for (const conv of savedConversations) {
          const messages = await pm.current.getMessages(conv.id);
          addConversation({
            ...conv,
            messages
          });
        }

        initialized.current = true;
      } catch (error) {
        console.error('[Persistence] Init error:', error);
      }
    };

    initPersistence();
  }, []);

  // Auto-save conversations when they change
  useEffect(() => {
    if (!pm.current || !initialized.current) return;
    if (conversations.length === 0) return;

    const saveConversations = async () => {
      try {
        // Save each conversation (debounced in practice)
        for (const conv of conversations) {
          await pm.current.saveConversation({
            id: conv.id,
            title: conv.title,
            timestamp: conv.timestamp,
            model: conv.model
          });

          // Save all messages
          for (const msg of conv.messages) {
            await pm.current.saveMessage({
              id: `${conv.id}-${msg.timestamp}`,
              conversationId: conv.id,
              role: msg.role,
              content: msg.content,
              timestamp: msg.timestamp
            });
          }
        }
      } catch (error) {
        console.error('[Persistence] Save error:', error);
      }
    };

    // Debounce saves (only save after 1 second of inactivity)
    const timeout = setTimeout(saveConversations, 1000);
    return () => clearTimeout(timeout);
  }, [conversations]);

  return {
    isInitialized: initialized.current,
    persistenceManager: pm.current
  };
}

export default usePersistence;
